import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, STATUS_LABELS, STATUS_CLASS, PLAN_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FileText, Receipt, Copy, PenNib, Link as LinkIcon, WhatsappLogo, EnvelopeSimple, FlagCheckered, CheckCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InvoicePayment } from "@/components/InvoicePayment";

const TABS = [
  { id: "ativo", label: "Ativos" },
  { id: "aguardando_assinatura", label: "Aguardando Assinatura" },
  { id: "inadimplente", label: "Inadimplentes" },
  { id: "finalizado", label: "Concluídos" },
  { id: "todos", label: "Todos" },
];

export default function ActiveContracts() {
  const [tab, setTab] = useState("ativo");
  const [contracts, setContracts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [signOpen, setSignOpen] = useState(null);
  const [signature, setSignature] = useState("");
  const [openInv, setOpenInv] = useState(null);

  const load = async () => {
    const url = tab === "todos" ? "/contracts" : `/contracts?status=${tab}`;
    const { data } = await api.get(url);
    setContracts(data);
  };
  useEffect(() => { load(); }, [tab]);

  const openDetail = async (c) => {
    setDetail(c);
    const { data } = await api.get(`/invoices?contract_id=${c.contract_id}`);
    setInvoices(data);
  };

  const syncInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.invoice_id}/sync`);
      setInvoices((list) => list.map((x) => (x.invoice_id === data.invoice_id ? data : x)));
      toast[data.status === "pago" ? "success" : "info"](data.status === "pago" ? "Pagamento confirmado" : `Status Inter: ${data.inter_situacao || "sem alteração"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao sincronizar"); }
  };

  const esign = async () => {
    try {
      const { data } = await api.post(`/contracts/${detail.contract_id}/esign`);
      setDetail(data); load();
      toast.success("Contrato enviado para assinatura na Clicksign");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar para Clicksign"); }
  };

  const esignSync = async () => {
    try {
      const { data } = await api.post(`/contracts/${detail.contract_id}/esign/sync`);
      setDetail(data); load();
      toast[data.status === "ativo" ? "success" : "info"](data.status === "ativo" ? "Contrato assinado e ativado!" : `Status Clicksign: ${data.esign?.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao verificar"); }
  };

  const genInvoice = async () => {
    try {
      const { data } = await api.post(`/contracts/${detail.contract_id}/invoices`);
      setInvoices((list) => [data, ...list]);
      setOpenInv(data.invoice_id);
      toast.success(data.provider === "INTER" && data.inter_codigo ? "Boleto emitido no Banco Inter" : "Parcela gerada");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar parcela"); }
  };

  const sign = async () => {
    if (!signature.trim()) return;
    try {
      await api.post(`/contracts/${signOpen.contract_id}/sign`, { signature });
      toast.success("Contrato assinado e ativado. Primeiro boleto gerado.");
      setSignOpen(null); setSignature("");
      load();
    } catch { toast.error("Erro"); }
  };

  const [finFor, setFinFor] = useState(null);
  const [finOpenInv, setFinOpenInv] = useState([]);
  const [finPend, setFinPend] = useState({ pintura: "", limpeza: "", aluguel: "" });
  const [finNotes, setFinNotes] = useState("");

  const openFinalize = async (c) => {
    const { data } = await api.get(`/invoices?contract_id=${c.contract_id}`);
    setFinOpenInv(data.filter((i) => !["pago", "cancelado"].includes(i.status)));
    setFinPend({ pintura: "", limpeza: "", aluguel: "" }); setFinNotes("");
    setFinFor(c);
  };

  const finalize = async () => {
    const pendencias = Object.entries(finPend).filter(([, v]) => parseFloat(v) > 0)
      .map(([category, v]) => ({ category, amount: parseFloat(v), description: "" }));
    try {
      const { data } = await api.post(`/contracts/${finFor.contract_id}/finalize`, { pendencias, notes: finNotes });
      toast[data.status === "finalizado" ? "success" : "warning"](data.status === "finalizado" ? "Contratação concluída sem pendências" : "Contratação finalizada com pendências — enviada para Inadimplência");
      setFinFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao finalizar"); }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-1">Portfólio</div>
          <h1 className="font-display text-4xl tracking-tight">Contratações Ativas</h1>
        </div>
        <Link to="/garantia"><Button data-testid="nav-new-contract" className="btn-primary"><FileText size={16} className="mr-2" /> Nova Contratação</Button></Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[var(--line)]" data-testid="contract-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-[var(--emerald)] text-[var(--emerald)]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="card-flat overflow-hidden" data-testid="contracts-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 eyebrow">
            <tr>
              <th className="text-left p-4">Inquilino</th>
              <th className="text-left p-4">Imóvel</th>
              <th className="text-left p-4">Plano</th>
              <th className="text-left p-4">Aluguel</th>
              <th className="text-left p-4">Prêmio</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Início</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.contract_id} className="border-t border-[var(--line)] hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(c)}>
                <td className="p-4 font-medium">{c.tenant_name}<div className="text-[11px] text-slate-400 mono">{c.tenant_cpf}</div></td>
                <td className="p-4 text-slate-600 max-w-xs truncate">{c.property_address}</td>
                <td className="p-4">{PLAN_LABELS[c.plan]} · {c.coverage_multiplier}x</td>
                <td className="p-4 mono">{fmtBRL(c.rent_amount)}</td>
                <td className="p-4 mono text-slate-500">{fmtBRL(c.monthly_premium)}</td>
                <td className="p-4"><span className={`status-pill ${STATUS_CLASS[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                <td className="p-4 text-slate-500">{fmtDate(c.start_date)}</td>
                <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                  {c.status === "aguardando_assinatura" && (
                    <Button size="sm" className="btn-primary" onClick={() => setSignOpen(c)} data-testid={`sign-${c.contract_id}`}><PenNib size={12} className="mr-1" /> Assinar</Button>
                  )}
                  {["ativo", "inadimplente"].includes(c.status) && (
                    <Button size="sm" variant="outline" onClick={() => openFinalize(c)} data-testid={`finalize-${c.contract_id}`}><FlagCheckered size={12} className="mr-1" /> Finalizar</Button>
                  )}
                </td>
              </tr>
            ))}
            {contracts.length === 0 && (
              <tr><td colSpan="8" className="p-10 text-center text-slate-400">Nenhum contrato nesta categoria.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="contract-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl">{detail.tenant_name}</DialogTitle>
                <div className="text-sm text-slate-500 mono">{detail.tenant_cpf} · {detail.tenant_email}</div>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Info label="Plano" value={`${PLAN_LABELS[detail.plan]} · ${detail.coverage_multiplier}x`} />
                <Info label="Aluguel" value={fmtBRL(detail.rent_amount)} />
                <Info label="Prêmio mensal" value={fmtBRL(detail.monthly_premium)} />
              </div>
              <div className="mt-4">
                <div className="eyebrow mb-2">Imóvel</div>
                <div className="text-sm">{detail.property_address}</div>
                <div className="text-xs text-slate-500 mt-1">Proprietário: {detail.owner_name}</div>
              </div>

              {(detail.status === "aguardando_assinatura" || detail.esign) && (
                <div className="mt-6 p-4 rounded-md border border-[var(--line)] bg-slate-50" data-testid="esign-section">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="eyebrow flex items-center gap-1.5"><PenNib size={12} /> Assinatura digital — Clicksign</div>
                      {detail.esign ? (
                        <div className="text-xs text-slate-600 mt-1">
                          Envelope <span className="mono">{detail.esign.envelope_id}</span> · status <strong data-testid="esign-status">{detail.esign.status}</strong>
                          {detail.esign.signing_url && <> · <a className="underline text-[var(--emerald)]" href={detail.esign.signing_url} target="_blank" rel="noreferrer" data-testid="esign-link">link de assinatura</a></>}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 mt-1">Envia o contrato em PDF para o inquilino assinar com validade jurídica (e-mail/WhatsApp).</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!detail.esign && detail.status === "aguardando_assinatura" && (
                        <Button size="sm" className="btn-primary" onClick={esign} data-testid="esign-send-btn"><PenNib size={12} className="mr-1" /> Enviar p/ Clicksign</Button>
                      )}
                      {detail.esign && detail.status === "aguardando_assinatura" && (
                        <Button size="sm" variant="outline" onClick={esignSync} data-testid="esign-sync-btn">Verificar assinatura</Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {detail.portal_token && (
                <div className="mt-6 p-4 rounded-md bg-[var(--emerald-soft)] border border-[var(--emerald)]/20" data-testid="detail-portal-section">
                  <div className="flex items-center gap-2 mb-2">
                    <LinkIcon size={16} color="#1E2A44" weight="duotone" />
                    <div className="eyebrow text-[var(--emerald)]">Portal do inquilino</div>
                  </div>
                  <div className="text-xs text-slate-600 mb-2">Envie este link para o inquilino ver contrato, pagar faturas e assinar — sem precisar de login.</div>
                  <div className="flex items-center gap-2 bg-white border border-[var(--line)] rounded px-3 py-2">
                    <input readOnly value={`${window.location.origin}/portal/${detail.portal_token}`} className="flex-1 text-xs mono bg-transparent outline-none" data-testid="portal-link-input" />
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal/${detail.portal_token}`); toast.success("Link copiado"); }} className="text-slate-500 hover:text-slate-900" data-testid="portal-copy-btn"><Copy size={14} /></button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {detail.tenant_phone && (
                      <a
                        data-testid="portal-whatsapp"
                        href={`https://wa.me/55${(detail.tenant_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${detail.tenant_name}! Sua garantia locatícia Safimob está pronta. Acesse seu portal para ver o contrato e pagar as faturas: ${window.location.origin}/portal/${detail.portal_token}`)}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#25D366] text-white text-xs hover:opacity-90"
                      >
                        <WhatsappLogo size={14} weight="fill" /> WhatsApp
                      </a>
                    )}
                    {detail.tenant_email && (
                      <a
                        data-testid="portal-email"
                        href={`mailto:${detail.tenant_email}?subject=${encodeURIComponent("Sua garantia Safimob — acesso ao portal")}&body=${encodeURIComponent(`Olá ${detail.tenant_name},\n\nSua garantia locatícia Safimob está pronta.\n\nAcesse seu portal:\n${window.location.origin}/portal/${detail.portal_token}\n\nSafimob Garantidora de Aluguel`)}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--line)] text-xs hover:bg-slate-50"
                      >
                        <EnvelopeSimple size={14} /> Email
                      </a>
                    )}
                    <a
                      data-testid="portal-open"
                      href={`/portal/${detail.portal_token}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--line)] text-xs hover:bg-slate-50 ml-auto"
                    >
                      Abrir portal <LinkIcon size={12} />
                    </a>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow">Cobranças ({invoices.length})</div>
                  {["ativo", "inadimplente"].includes(detail.status) && (
                    <Button size="sm" variant="outline" onClick={genInvoice} data-testid="gen-invoice-btn"><Receipt size={12} className="mr-1" /> Gerar parcela mensal</Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto border border-[var(--line)] rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left p-2">Vencimento</th>
                        <th className="text-left p-2">Valor</th>
                        <th className="text-left p-2">Tipo</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((i) => (
                        <React.Fragment key={i.invoice_id}>
                          <tr className="border-t border-[var(--line)]">
                            <td className="p-2">{fmtDate(i.due_date)}</td>
                            <td className="p-2 mono">{fmtBRL(i.amount)}</td>
                            <td className="p-2">{i.type === "ativacao" ? "1ª parcela" : "Recorrente"} <span className="text-slate-400">· {i.provider === "INTER" ? "Inter" : "simulado"}</span></td>
                            <td className="p-2"><span className={`status-pill ${STATUS_CLASS[i.status]}`}>{STATUS_LABELS[i.status]}</span></td>
                            <td className="p-2 text-right">
                              <button onClick={() => setOpenInv(openInv === i.invoice_id ? null : i.invoice_id)} className="text-[var(--emerald)] underline" data-testid={`inv-toggle-${i.invoice_id}`}>
                                {openInv === i.invoice_id ? "Fechar" : "Pagamento"}
                              </button>
                            </td>
                          </tr>
                          {openInv === i.invoice_id && (
                            <tr className="border-t border-[var(--line)] bg-slate-50/60">
                              <td colSpan="5" className="p-3"><InvoicePayment invoice={i} onSync={syncInvoice} /></td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {invoices.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-slate-400">Sem cobranças</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Finalize dialog */}
      <Dialog open={!!finFor} onOpenChange={() => setFinFor(null)}>
        <DialogContent className="max-w-lg" data-testid="finalize-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Finalizar contratação</DialogTitle></DialogHeader>
          {finFor && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-600">{finFor.tenant_name} · {finFor.property_address}</div>
              <div className="p-3 rounded-md border border-[var(--line)] bg-slate-50">
                <div className="eyebrow mb-1">Taxas / faturas em aberto</div>
                {finOpenInv.length === 0 ? <div className="text-emerald-700 flex items-center gap-1" data-testid="fin-no-invoices"><CheckCircle size={14} /> Nenhuma fatura pendente</div> : (
                  <ul className="space-y-1" data-testid="fin-open-invoices">
                    {finOpenInv.map((i) => <li key={i.invoice_id} className="flex justify-between"><span>{i.description}</span><span className="mono">{fmtBRL(i.amount)}</span></li>)}
                  </ul>
                )}
                <div className="text-[11px] text-slate-400 mt-1">Entram automaticamente como pendência "Taxa".</div>
              </div>
              <div>
                <div className="eyebrow mb-2">Outras pendências (deixe em branco se não houver)</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["pintura", "Pintura"], ["limpeza", "Limpeza"], ["aluguel", "Aluguel em aberto"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs text-slate-500 mb-1 block">{l} (R$)</Label><Input data-testid={`fin-${k}`} type="number" value={finPend[k]} onChange={(e) => setFinPend({ ...finPend, [k]: e.target.value })} placeholder="0,00" /></div>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Observações</Label><Input data-testid="fin-notes" value={finNotes} onChange={(e) => setFinNotes(e.target.value)} placeholder="Ex.: vistoria realizada em…" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinFor(null)}>Cancelar</Button>
            <Button className="btn-primary" onClick={finalize} data-testid="fin-confirm"><FlagCheckered size={14} className="mr-2" /> Finalizar contratação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign dialog */}
      <Dialog open={!!signOpen} onOpenChange={() => setSignOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Assinar contrato</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-3">
            <p><strong>{signOpen?.tenant_name}</strong> — {fmtBRL(signOpen?.rent_amount || 0)}/mês</p>
            <p className="text-xs mt-1">Ao assinar, o boleto de ativação será gerado automaticamente via Asaas.</p>
          </div>
          <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Nome completo do signatário" className="font-display italic" data-testid="ac-sign-input" />
          <Button className="btn-primary mt-4" onClick={sign} data-testid="ac-sign-confirm"><PenNib size={16} className="mr-2" /> Assinar & Ativar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value }) {
  return <div className="p-3 rounded-md bg-slate-50 border border-slate-100"><div className="eyebrow">{label}</div><div className="metric-value text-lg mt-1">{value}</div></div>;
}
